import React, { useState, useMemo } from 'react';

export type AvatarKind = 'company' | 'person';

interface AvatarProps {
  src?: string | null;
  /** Строка для вычисления инициалов и цвета (имя компании или ФИО). */
  name?: string;
  /** Явные инициалы. Если не заданы — считаем из `name`. */
  initials?: string;
  /** Размер кружка в пикселях. */
  size?: number;
  /** Влияет на то, как вычисляются инициалы. */
  kind?: AvatarKind;
  className?: string;
}

// Детерминированная палитра градиентов — у одной и той же
// сущности всегда одинаковый фон между перерисовками.
const GRADIENTS: [string, string][] = [
  ['#E3282A', '#FF6A6C'], // красный — основной акцент проекта
  ['#2F6BFF', '#6AA2FF'], // синий
  ['#1F9D55', '#4CCF8A'], // зелёный
  ['#F59E0B', '#FBBF24'], // янтарный
  ['#8B5CF6', '#B794F4'], // фиолетовый
  ['#0EA5E9', '#38BDF8'], // голубой
  ['#EC4899', '#F472B6'], // розовый
  ['#64748B', '#94A3B8'], // нейтральный
];

function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function initialsFromName(name: string, kind: AvatarKind): string {
  const cleaned = (name || '')
    .replace(/^(ООО|ОАО|ЗАО|ПАО|ИП|АО)\s+/i, '')
    .replace(/["«»]/g, '')
    .trim();
  const words = cleaned.split(/\s+/).filter(Boolean);

  if (words.length === 0) return kind === 'person' ? '•' : '?';
  if (words.length === 1) {
    return kind === 'person'
      ? words[0][0].toUpperCase()
      : words[0].slice(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  name = '',
  initials,
  size = 40,
  kind = 'person',
  className = '',
}) => {
  const [errored, setErrored] = useState(false);
  const hasPhoto = Boolean(src) && !errored;

  const computedInitials = useMemo(
    () => (initials && initials.length ? initials : initialsFromName(name, kind)),
    [initials, name, kind]
  );

  const [from, to] = useMemo(() => {
    const key = name || initials || 'empty';
    return GRADIENTS[hashCode(key) % GRADIENTS.length];
  }, [name, initials]);

  const fontSize = Math.max(11, Math.floor(size * 0.4));

  const style: React.CSSProperties = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
  };

  if (hasPhoto) {
    return (
      <div
        className={`rounded-full overflow-hidden bg-[#F4F5F7] flex items-center justify-center shrink-0 ${className}`}
        style={style}
      >
        <img
          src={src as string}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => setErrored(true)}
        />
      </div>
    );
  }

  return (
    <div
      className={`rounded-full flex items-center justify-center text-white font-semibold select-none shrink-0 ${className}`}
      style={{
        ...style,
        background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
        fontSize,
        letterSpacing: '0.02em',
      }}
      aria-label={name || 'Аватар'}
      title={name}
    >
      {computedInitials}
    </div>
  );
};

export default Avatar;
